// @ts-nocheck — cs.jsx 공용 컴포넌트 타입 미정의 회피 (HeatmapScreen 패턴)
// SimScreen.tsx — 솟다 실시간 모의투자 포트폴리오 대시보드
// design doc §14 최소 루프 + §17: 거래(매수/매도)는 종목 상세(StockDetailScreen)로 위임
//   - 이 화면 = 총자산/보유 현황 + 종목 검색 → 종목 상세(차트) 진입
// cs-tokens 단일 출처(인라인 hex 0). 손익 색 = 한국식(상승 rise / 하락 fall).
import '../cs-tokens.css';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CSBtn, CSInput, CSModal, CSBadge, CSRow, CSIcon, CSIconBtn, CSCard, CSTabs, BullCoin } from '../_components/cs';
import { apiFetch, USE_MOCK } from '../../lib/api';
import { isLeveragedEtf, LEVERAGED_BADGE } from '@core/sim/market/leveragedEtf';
import { cachedFetch, peekCache } from '../../lib/prefetch';
import { Skeleton, SkeletonCard } from '../../components/common/Skeleton';
import { useAuth } from '../../lib/useAuth';
import { eyeGrade, resolveEyeGrade, pnlColor, EYE_GRADE_TIERS } from '@core/sim/engine/portfolio';
import { SIM_SYMBOLS, MARKET_LABEL } from '@core/sim/market/simSymbols';
import { koMatch } from '@core/sim/screener/koSearch';
import { subscribeMiniTickers } from '../../data-access/binanceWs';
import { subscribeYahooTicks } from '../../data-access/yahooWs';
import { applyLivePrice } from '@core/sim/market/liveParsers';
import { RollingNumber } from '../_components/RollingNumber';
import { useActiveMacroEvents } from '../../hooks/useMacroEvents';
import { useActiveBuyOrNotPolls } from '../../lib/useBuyOrNotPolls';
import { extractLossCards } from '@core/sim/behavior/behaviorEngine'; // ⑤ 복기할 손실 건수
import { usePullToRefresh } from '../../lib/usePullToRefresh'; // 당겨서 새로고침 (탭 루트 헤더 제거 대체)
import { PullIndicator } from '../_components/PullIndicator';
import { useEquippedCompanion } from '../../hooks/useEquippedCompanion';
import { EquippedCosmeticMini } from '../_components/SocialCosmetic';
import { companionLine } from '../companion/companionVoice';
import { NetworkHint } from '../../components/common/NetworkHint';

const fmtBull = (n) => Math.round(n || 0).toLocaleString('ko-KR');
const fmtPrice = (n, cur) =>
  cur === 'USD'
    ? '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : Number(n).toLocaleString('ko-KR', { maximumFractionDigits: 0 }) + '원';
// 목록 행 전용 가격 — CSRow의 sub는 `whiteSpace: nowrap` + ellipsis라 한 줄을 못 넘긴다.
// 7자리 원화가 둘이면 실제로 끊겼다: SK하이닉스가 "현재가 1,730,000원 · 평단 2,178,29…"로 잘렸고
// 비트코인도 "평단 $61,480…"이 됐다. 코인·고가주를 담으면 누구나 겪는다.
// 목록에서는 자릿수 감이면 충분하고 정확한 값은 종목 상세에서 본다 → 큰 값만 줄인다.
//   · 원화 100만 이상 → 만 단위(1,730,000 → 173만원 · 2,178,294 → 217.8만원)
//   · USD 1,000 이상 → 소수점 제거($61,480.88 → $61,481). 200달러대는 그대로 둔다.
const fmtPriceCompact = (n, cur) => {
  const v = Number(n) || 0;
  if (cur === 'USD') return '$' + v.toLocaleString('en-US', { maximumFractionDigits: v >= 1000 ? 0 : 2 });
  if (v >= 1_000_000) return (v / 10_000).toLocaleString('ko-KR', { maximumFractionDigits: 1 }) + '만원';
  return v.toLocaleString('ko-KR', { maximumFractionDigits: 0 }) + '원';
};
// 보유 수량 — 금액 단위라 소수점 (0.0012주 등). 큰 수는 정수
const fmtQty = (n) => {
  const v = Number(n) || 0;
  if (v >= 100) return Math.round(v).toLocaleString('ko-KR');
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(4);
};

const nameOf = (symbol) => SIM_SYMBOLS.find((s) => s.symbol === symbol)?.name || symbol;
// 손익 색 — P2 §20: simEngine 단일 출처(pnlColor)로 통합. 0은 중립(매수 직후 "+0.00% 빨강" 방지)
const stockPath = (symbol) => `/stock/${encodeURIComponent(symbol)}`;

// 보유종목 유형 기호 아이콘 (A안 — 효신님 결정 2026-06-14). asset_class·currency 기반.
//   미국주식 $ / 한국주식 ₩ / 코인 ₿ · cs-tokens 색만(가짜 토큰 0).
//   외부 로고 미사용 근거: 한국주식 로고 공개 API 부재(KRX·공공데이터·KIS 텍스트만) +
//   Clearbit 주식 로고 불안정(목업 검증서 코인만 로드) → 전 종목 일관 위해 기호 채택.
function AssetIcon({ p }) {
  const isCoin = p.asset_class === 'crypto' || /-USD$/.test(p.symbol || '');
  const isKr = !isCoin && p.currency === 'KRW';
  const [sym, bg, fg] = isCoin
    ? ['₿', 'var(--cs-gold-soft)', 'var(--cs-bull)']
    : isKr
      ? ['₩', 'var(--cs-fall-soft)', 'var(--cs-fall)']
      : ['$', 'var(--cs-income-soft)', 'var(--cs-accent)'];
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
      {sym}
    </div>
  );
}
// 섹션 헤더 — 시안 1:1(claude.ai/design): 명조 Nanum Myeongjo 17px/800 · 색은 --cs-ink(#1A1714, 시안과 정확 일치).
//   heading 클래스(.cs-accent-heading)는 잉크그린 강조색까지 입혀 못 씀 → 명조 패밀리만 인라인.
//   margin-top은 컨테이너 gap(14) 위에 얹혀 시안 26px 근사(12) — 첫 헤더는 gap 없는 hero 직후라 별도.
function SecHeader({ children, right, first = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: first ? '4px 2px 10px' : '12px 2px 10px' }}>
      <h2 style={{ fontFamily: "'Nanum Myeongjo', var(--cs-font-serif)", fontSize: 17, fontWeight: 800, color: 'var(--cs-ink)', letterSpacing: '-0.4px', whiteSpace: 'nowrap', margin: 0 }}>{children}</h2>
      {right}
    </div>
  );
}

// 부가 진입점 세그먼트 공용 리스트 — 참여·도구·기록 세 탭이 같은 컴포넌트를 쓴다(2026-07-26 시각 통일).
//   §G-2 테두리 제거 → 그림자(--cs-shadow-1) 카드 + 내부는 divider.
//   §G-1 아이콘 배경 무채색(--cs-surface-2) 통일 — 이전엔 탭마다 tint 4색이라 §G 위반이었다.
//   색을 쓰는 곳은 라이브 배지(accent) 하나뿐: "지금 참여할 게 있다"는 유일하게 색이 필요한 신호.
function SubTabList({ rows, nav }) {
  return (
    <div style={{ background: 'var(--cs-surface)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--cs-shadow-1)' }}>
      {rows.map((r, i) => (
        <button key={r.to} onClick={() => nav(r.to)} style={{ width: '100%', border: 'none', borderTop: i > 0 ? '1px solid var(--cs-surface-2)' : 'none', background: 'transparent', display: 'flex', alignItems: 'center', gap: 13, padding: '15px 16px', cursor: 'pointer', textAlign: 'left' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--cs-surface-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--cs-ink-2)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{r.icon}</svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--cs-ink)', letterSpacing: '-0.3px' }}>{r.label}</div>
            <div style={{ fontSize: 12.5, color: r.live ? 'var(--cs-accent)' : 'var(--cs-mute)', fontWeight: r.live ? 700 : 400, marginTop: 2 }}>
              {r.live && <i style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: 'var(--cs-accent)', marginRight: 5, verticalAlign: 'middle' }} />}{r.sub}
            </div>
          </div>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--cs-mute-2)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      ))}
    </div>
  );
}

// 홈·투자 공통 포트폴리오 — prefetch 공유 캐시 키(#1·#5). 홈이 받아둔 걸 투자가 재사용해 빈화면 제거.
const PF_URL = '/api/mobile/sim/portfolio?league=swing';

// ⚠ 임시(mock 비교용) — VITE_USE_MOCK=true 일 때만. 시안 카피 기준 가짜 포트폴리오.
//    데스크톱 브라우저에서 로그인 없이 모의투자 풀화면을 BEFORE 비교하려는 용도.
//    정식 이식(SimScreen 재설계) 후 제거 가능.
const SIM_MOCK_PF = {
  total_value_bull: 10358200,
  pnl_bull: 358200,
  pnl_pct: 3.58,
  holdings_value_bull: 5538200,
  account: { investable_bull: 4820000, cash_bull: 0 },
  positions: [
    { symbol: 'AAPL', asset_class: 'us', currency: 'USD', cur_price: 234.5, avg_price: 210, quote_ok: true, value_bull: 1820000, pnl_bull: 190000, pnl_pct: 11.7 },
    { symbol: '005930.KS', asset_class: 'kr', currency: 'KRW', cur_price: 71200, avg_price: 68000, quote_ok: true, value_bull: 1560000, pnl_bull: 70000, pnl_pct: 4.7 },
    { symbol: 'BTC-USD', asset_class: 'crypto', currency: 'USD', cur_price: 98200, avg_price: 95000, quote_ok: true, value_bull: 1420000, pnl_bull: 46000, pnl_pct: 3.4 },
    { symbol: 'NVDA', asset_class: 'us', currency: 'USD', cur_price: 138, avg_price: 145, quote_ok: true, value_bull: 738200, pnl_bull: -38000, pnl_pct: -4.9 },
  ],
};

// isTabRoot — 모의투자 탭(=게임 탭 재편)의 루트로 마운트될 때 true.
//   true: 뒤로가기 숨김(탭 루트라 갈 곳 없음) + 트레이딩 도구 그리드 노출(허브 역할)
//   false(기본): 기존 /sim 푸시 화면 동작 유지 (뒤로가기 표시)
// design doc: 2026-06-09-sotda-S-game-tab-to-sim-hub-design §3 변경 ②
export default function SimScreen({ isTabRoot = false } = {}) {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const emptyCompanion = useEquippedCompanion(['empty_state', 'nick_avatar']);
  const cachedPf = peekCache(PF_URL); // 홈이 받아둔 캐시 즉시 사용(#1·#5) — 첫 진입 빈화면 스킵
  const [pf, setPf] = useState(USE_MOCK ? SIM_MOCK_PF : cachedPf);
  const [loading, setLoading] = useState(USE_MOCK ? false : !cachedPf);
  const [err, setErr] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [gradeInfoOpen, setGradeInfoOpen] = useState(false); // P2 §13 — 안목 등급 설명 모달
  const [search, setSearch] = useState('');
  const [lossCount, setLossCount] = useState(0); // ⑤ 복기 대기 손실 매도 건수
  // 부가 진입점 세그먼트(§1.1 C안) — 참여·도구·기록을 탭 분리해 세로 스크롤 감축. 기본=참여(6엔진 발견성)
  const [subTab, setSubTab] = useState('join');
  const subTabRef = useRef<HTMLDivElement>(null); // 탭 전환 시 세그먼트를 뷰포트 상단으로 고정(높이 급변 대응)
  // 참여 섹션(6엔진 상시 진입점 — ②설계 Q3) 라이브 배지. 금융탭과 동일 훅 재사용(활성 수만)
  const macroCount = useActiveMacroEvents().length;
  // ⚠ 2026-07-26: 예전엔 활성 폴 **전체**(종목+소비재)를 셌는데, 살까말까 화면은 `kind=stock` 탭이 기본이라
  //   "결정 대기 3건"을 보고 들어가면 2건만 보이는 불일치가 났다(효신님 ③ 지적).
  //   진입 후 첫 화면과 같은 기준(stock)으로 센다. 소비재는 화면 안 탭에서 발견되게 둔다.
  //   2026-09-17 B안(효신님 결정): 살까말까는 유저가 모여야 성립하는 기능이라 유저 확보 전까지 **잠금**.
  //   서버 토글 `buy_or_not_room_enabled`가 OFF면 목록 API가 []를 돌려주므로(예시 폴 2건도 안 옴)
  //   활성 폴이 0건이면 참여 섹션의 행 자체를 숨긴다 — 코드 삭제 없이 토글 하나로 재개 가능.
  //   FinanceScreen 배너는 이미 같은 조건(활성 0건 → 숨김)이라 이 화면만 맞추면 진입점이 모두 닫힌다.
  const buyOrNotPolls = useActiveBuyOrNotPolls();
  const buyOrNotVisible = buyOrNotPolls.length > 0;
  const buyOrNotCount = buyOrNotPolls.filter((p) => p.kind === 'stock').length;

  // 갱신 전략 (UX 검수 P1): 초기=loading 스피너 / 이후=silent(기존 숫자 유지·실패해도 stale 유지)
  //   + 앱 복귀(visible) 30s 경과 시 재검증 + 가시 상태 60s 폴링 + "마지막 갱신 HH:MM" 투명성.
  const [lastAt, setLastAt] = useState(null);
  const lastFetchRef = useRef(0);
  const load = useCallback(async (silent = false) => {
    if (USE_MOCK) return; // ⚠ 임시(mock 비교용) — fetch 스킵, SIM_MOCK_PF 유지
    if (!silent) { setLoading(true); setErr(false); }
    try {
      const data = await cachedFetch(PF_URL, { cache: 'no-store' }, silent); // silent(폴링)=fresh, 첫진입=캐시 활용
      setPf(data); setErr(false);
      lastFetchRef.current = Date.now(); setLastAt(Date.now());
    } catch (e) {
      // 401 = 세션 무효(계정 삭제·토큰 만료) → 'auth'로 구분해 "네트워크 에러"로 오인 표시 방지
      if (!silent) setErr(e?.status === 401 ? 'auth' : true); // silent 실패 = 직전 데이터 유지 (stale-while-revalidate)
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ⑤ 손실 매도 건수 집계 → 복기 배너 (실패 박물관 진입점)
  const loadLoss = useCallback(() => {
    if (USE_MOCK) { setLossCount(2); return; } // ⚠ 임시(mock 비교용) — 복기 배너 표시용
    if (!user) return;
    apiFetch('/api/mobile/sim/trades?league=swing&limit=300', { cache: 'no-store' })
      .then((t) => setLossCount(extractLossCards(t?.trades || []).length))
      .catch(() => { /* 무시 — 상시 배너는 0건 문구로 우아하게 강등 */ });
  }, [user]);
  useEffect(() => { loadLoss(); }, [loadLoss]);

  // 호흡2 — 유저 백분위 벤치마크(안목 등급 3단계). 실패/씨앗 단계 시 percentile null → 절대등급 폴백.
  const [bench, setBench] = useState(null);
  // 쿼리는 react-router로 읽는다 — 네이티브(HashRouter)에선 window.location.search가 항상 ''
  const [searchParams] = useSearchParams();
  const loadBench = useCallback(() => {
    // dev 미리보기 — ?benchPreview=grow|mature&pct=12&rank=149&count=1240 강제 주입.
    //   현재 씨앗 단계(활성<30)라 실서버는 percentile=null → 성장/성숙 UI를 SE3·웹서 미리 육안.
    try {
      const sp = searchParams;
      const pv = sp.get('benchPreview');
      if (pv === 'grow' || pv === 'mature' || pv === 'seed') {
        setBench({
          stage: pv,
          percentile: pv === 'seed' ? null : Number(sp.get('pct') ?? 12),
          rank: Number(sp.get('rank') ?? 149),
          active_count: Number(sp.get('count') ?? 1240),
          my_pnl_pct: null,
        });
        return;
      }
    } catch { /* SSR·URL 파싱 실패 무시 */ }
    if (USE_MOCK || !user) return;
    apiFetch('/api/mobile/sim/benchmark?league=swing', { cache: 'no-store' })
      .then((b) => setBench(b))
      .catch(() => { /* 무시 — resolveEyeGrade가 절대 수익률로 폴백 */ });
  }, [user, searchParams]);
  useEffect(() => { loadBench(); }, [loadBench]);

  // 호흡2 ③ — 단계 전환 1회성 안내. 직전 stage를 localStorage에 기억 → seed→grow/mature 승격 시 1회 배너.
  //   첫 방문(기억 없음)·동일 단계·강등은 미표시. 닫으면 null.
  const [stageBanner, setStageBanner] = useState<string | null>(null);
  useEffect(() => {
    const stage = bench?.stage;
    if (!stage) return;
    try {
      const prev = localStorage.getItem('sotda_bench_stage');
      if (prev && prev !== stage && stage !== 'seed') setStageBanner(stage);
      localStorage.setItem('sotda_bench_stage', stage);
    } catch { /* 프라이빗 모드 등 무시 */ }
  }, [bench]);

  // 헤더 새로고침 = 포트폴리오 + 손실 건수 통합 갱신
  const refreshAll = useCallback(() => { load(); loadLoss(); }, [load, loadLoss]);
  // 당겨서 새로고침 — 탭 루트에서 헤더 새로고침 버튼 대체. onRefresh는 실제 fetch 완료를 await 해야
  // 인디케이터가 그때까지 유지됨. load(silent)는 Promise 반환.
  const { bind: ptrBind, pull, refreshing } = usePullToRefresh(() => Promise.all([load(true), loadLoss()]));

  // 앱 복귀 재검증 + 가시 60s 폴링 — 신선도를 라우팅 부산물에 안 맡김 (시세 20분 지연이라 60s면 충분)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchRef.current > 30000) {
        load(true); loadLoss();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 30000); // 60s→30s (서버 quote 캐시 30s와 동기 — 실시간 체감 개선)
    return () => { document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
  }, [load, loadLoss]);

  // 보유 코인 실시간 — 바이낸스 miniTicker 멀티스트림(~1초 주기)으로 평가액·손익·총계 갱신.
  // 주식은 30s 폴링 유지 (Yahoo 폴링 비용·주말 휴장). 구독 키 = 코인 심볼 목록(포지션 변동 시 재구독).
  const cryptoSyms = (pf?.positions || [])
    .map((p) => p.symbol)
    .filter((s) => /-USD$/.test(s))
    .sort()
    .join(',');
  useEffect(() => {
    if (USE_MOCK || !cryptoSyms) return; // ⚠ 임시: mock 모드에선 실시간 구독 스킵

    const unsub = subscribeMiniTickers(cryptoSyms.split(','), (sym, price) => {
      if (document.visibilityState !== 'visible') return; // 백그라운드 setState 낭비 방지
      setPf((prev) => (prev ? applyLivePrice(prev, sym, price) : prev));
    });
    return unsub;
  }, [cryptoSyms]);

  // 보유 주식(미국·한국) 틱 실시간 — Yahoo 스트리머 (코인과 동일 applyLivePrice 경로, 30s 폴링은 폴백)
  const stockSyms = (pf?.positions || [])
    .map((p) => p.symbol)
    .filter((s) => !/-USD$/.test(s))
    .sort()
    .join(',');
  useEffect(() => {
    if (USE_MOCK || !stockSyms) return; // ⚠ 임시: mock 모드에선 실시간 구독 스킵

    const unsub = subscribeYahooTicks(stockSyms.split(','), (tk) => {
      if (document.visibilityState !== 'visible') return;
      setPf((prev) => (prev ? applyLivePrice(prev, tk.id, tk.price) : prev));
    });
    return unsub;
  }, [stockSyms]);

  const acc = pf?.account;
  const positions = pf?.positions || [];
  const total = pf?.total_value_bull ?? 0;
  const pnl = pf?.pnl_bull ?? 0;
  const pnlPct = pf?.pnl_pct ?? 0;
  // 호흡2: 3단계 적응형 안목 등급(씨앗=절대 수익률 / 성장=혼합 / 성숙=유저 백분위). bench 없으면 절대 폴백.
  const resolvedGrade = resolveEyeGrade({ stage: bench?.stage || 'seed', pnlPct, percentile: bench?.percentile ?? null });
  const grade = resolvedGrade.grade;

  // 한글 유사검색 — "ㅅ → 사 → 삼" 타이핑 도중에도 매칭 (초성·조합중 음절, koSearch TDD)
  const filtered = SIM_SYMBOLS.filter(
    (s) => !search || koMatch(s.name, search) || s.symbol.toUpperCase().includes(search.trim().toUpperCase()),
  );

  // 전체 검색 — 유니버스 밖 종목(KRX 전 종목 2,700+·미국 전 종목·신규 IPO)은 서버 검색으로 보강.
  // 2자 이상 + 300ms 디바운스 (Yahoo·KRX rate limit 배려) · 로컬 결과와 symbol 중복 제거
  const [remote, setRemote] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setRemote([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      apiFetch(`/api/market/search?q=${encodeURIComponent(q)}`)
        .then((r) => { if (!cancelled) setRemote(r?.results || []); })
        .catch(() => { if (!cancelled) setRemote([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);
  const localSyms = new Set(filtered.map((s) => s.symbol));
  const remoteOnly = remote.filter((r) => !localSyms.has(r.symbol));

  // 미로그인 가드 — 로그인해야 모의투자 가능 (서울 Supabase 인증)
  // ⚠ 임시(mock 비교용): USE_MOCK 이면 가드 우회 → 데스크톱서 풀화면 비교
  if (!USE_MOCK && !authLoading && !user) {
    return (
      <div className="cs-root" data-temp="warm" style={{ minHeight: '100%', background: 'var(--cs-bg)', color: 'var(--cs-ink)' }}>
        {/* 탭 루트에선 헤더 제거(다른 탭과 통일) — 비탭루트만 뒤로가기 유지 */}
        {!isTabRoot && (
          <header style={{ padding: '12px 16px 10px', display: 'flex', alignItems: 'center' }}>
            <CSIconBtn onClick={() => nav(-1)} label="뒤로"><CSIcon name="back" size={22} /></CSIconBtn>
          </header>
        )}
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><BullCoin size={44} /></div>
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>실시간 모의투자</div>
          <div style={{ fontSize: 13, color: 'var(--cs-mute)', marginBottom: 24, lineHeight: 1.6 }}>
            로그인하면 <b>활동으로 모은 BULL</b>로<br />실제 시세에 가상 투자할 수 있어요
          </div>
          <CSBtn variant="primary" size="lg" full onClick={() => nav('/login')}>로그인하기</CSBtn>
        </div>
      </div>
    );
  }

  return (
    // ⚠ className="cs-root" 필수 — --cs-accent 등 composite 토큰이 .cs-root 스코프에만 정의됨(:root X)
    // ⚠ position:relative 필수 — CSModal(absolute inset:0)의 기준 (positioning trap 회피)
    <div ref={ptrBind} className="cs-root" data-temp="warm" style={{ position: 'relative', minHeight: '100%', background: 'var(--cs-bg)', color: 'var(--cs-ink)', paddingBottom: 90 }}>
      {/* 탭 루트(=/games)에선 헤더 제거 — 다른 4탭(홈·금융·커뮤니티·MY)과 통일. 고정 헤더가 유일하게
          모의투자 탭에만 남아 거슬리던 현상 정합(버그 7). 새로고침은 아래 '당겨서 새로고침'으로 대체.
          비탭루트(SimPage 등)는 뒤로가기가 필요하므로 헤더 유지. */}
      {!isTabRoot && (
        <header style={{ position: 'sticky', top: 0, zIndex: 4, background: 'var(--cs-bg)', padding: '12px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <CSIconBtn onClick={() => nav(-1)} label="뒤로"><CSIcon name="back" size={22} /></CSIconBtn>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>모의투자</div>
          <CSIconBtn onClick={refreshAll} label="새로고침"><CSIcon name="refresh" size={20} /></CSIconBtn>
        </header>
      )}

      {/* 당겨서 새로고침 인디케이터 — 스크롤 최상단에서 아래로 당기면 표시(항목 8) */}
      <PullIndicator pull={pull} refreshing={refreshing} />

      <div className="cs-stagger" style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* 에러 시 히어로 대체 카드 — "총자산 0" 단정·파산 오발동 차단 (UX 검수 결함 #2) */}
        {err && !loading && (
          <CSCard padded>
            <div style={{ textAlign: 'center', padding: '14px 0' }}>
              <div style={{ marginBottom: 8 }}><CSIcon name={err === 'auth' ? 'lock' : 'signal'} size={24} color="var(--cs-mute)" /></div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{err === 'auth' ? '로그인이 만료됐어요' : '포트폴리오를 불러오지 못했어요'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--cs-mute)', marginBottom: 14, lineHeight: 1.5 }}>{err === 'auth' ? '계정이 변경됐거나 로그인이 만료됐어요 · 다시 로그인해 주세요' : '네트워크를 확인하고 다시 시도해 주세요'}</div>
              {/* 전면 실패로 판정될 때만 노출 — VPN·DNS 앱 후킹 안내 */}
              {err !== 'auth' && <NetworkHint />}
              {err === 'auth'
                ? <CSBtn variant="secondary" size="md" onClick={() => nav('/login')}>다시 로그인</CSBtn>
                : <CSBtn variant="secondary" size="md" onClick={load}>다시 시도</CSBtn>}
            </div>
          </CSCard>
        )}

        {/* 호흡2 ③ — 단계 전환 1회성 안내 배너(seed→grow/mature 승격 시 1회). 닫기 가능. */}
        {stageBanner && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'rgba(154,111,26,0.12)', border: '1px solid rgba(214,176,98,0.4)', borderRadius: 12, padding: '11px 13px', marginBottom: 12 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="var(--cs-bull)" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M12 2l2.5 6.5L21 9l-5 4.5L17.5 21 12 17l-5.5 4L8 13.5 3 9l6.5-.5z" /></svg>
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--cs-ink)', lineHeight: 1.45, letterSpacing: '-0.2px' }}>
              {stageBanner === 'mature'
                ? <>솟다 유저 <b>{(bench?.active_count || 0).toLocaleString()}명</b> 돌파 · 이제 안목 등급이 <b>유저 백분위</b>로 매겨져요</>
                : <>솟다 유저가 늘었어요 · 안목 등급에 <b>유저 백분위</b>가 반영돼요</>}
            </span>
            <button type="button" onClick={() => setStageBanner(null)} aria-label="닫기" style={{ flexShrink: 0, background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--cs-mute)', display: 'inline-flex' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
        )}
        {/* 총자산 히어로 — 시안 1:1 이식(claude.ai/design 인라인 style 정확값, 효신님 결정 2026-06-14).
            폰트·크기·색·박스 전부 시안값 그대로. 손익 ▲%만·폐지수집소 제거는 효신님 결정. 에러 시 미표시 */}
        {!err && (
        <div style={{ background: 'var(--cs-hero-ink)', borderRadius: 20, padding: '22px 22px 0', color: 'var(--cs-on-ink)', boxShadow: 'var(--cs-shadow-hero)' }}>
          {/* 총자산 라벨 + 안목 배지 (골드 반투명 배경 + T3 grade-b 별+눈 배지 — 배칭E 채택 2026-07-10) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13.5, color: 'rgba(241,236,224,0.62)', fontWeight: 600, letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>
              총자산
              {lastAt && <span style={{ fontSize: 10.5, color: 'rgba(241,236,224,0.4)', marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}>{new Date(lastAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })} 갱신</span>}
            </span>
            {/* P2 §13 — 안목 등급 명시("안목") + 탭하면 설명 모달 (기존 의미불명 ★배지) */}
            <button type="button" onClick={() => setGradeInfoOpen(true)} aria-label="안목 등급 설명" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(154,111,26,0.22)', border: '1px solid rgba(214,176,98,0.4)', color: 'var(--cs-gold-on-ink)', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, letterSpacing: '-0.2px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <img src="/redesign-assets/brand/grade-b@128.png" alt="" width={14} height={14} style={{ objectFit: 'contain' }} />
              안목 {grade.label}
              <span style={{ fontSize: 10, opacity: 0.7 }}>ⓘ</span>
            </button>
          </div>
          {/* 큰 숫자 (명조 Nanum Myeongjo 42px — 시안 정확값) */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginTop: 4 }}>
            <span style={{ fontFamily: "'Nanum Myeongjo', var(--cs-font-serif)", fontSize: 42, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {loading ? <Skeleton w={180} h={40} style={{ display: 'inline-block' }} /> : <RollingNumber text={fmtBull(total)} />}
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--cs-gold-on-ink)', paddingBottom: 4 }}>BULL</span>
          </div>
          {/* 손익 ▲%만 (효신님 결정: 금액 생략 · 시안 색 #FF7E6E 한국식 상승빨강) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 9 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: pnl > 0 ? 'var(--cs-rise-on-ink)' : pnl < 0 ? 'var(--cs-fall-on-ink)' : 'rgba(241,236,224,0.6)', fontVariantNumeric: 'tabular-nums' }}>
              {pnl > 0 ? '▲' : pnl < 0 ? '▼' : '·'} {Math.abs(pnlPct).toFixed(2)}%
            </span>
          </div>
          {/* 호흡2 ③ A안 — 유저 백분위 칩(성장·성숙 단계만, 씨앗=숨김). rank·active_count 노출. */}
          {bench && bench.stage !== 'seed' && bench.percentile != null && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 11, background: 'rgba(255,255,255,0.07)', borderRadius: 999, padding: '5px 11px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--cs-gold-on-ink)" aria-hidden="true"><path d="M5 4h14v3a5 5 0 0 1-4 4.9V14h2.5l.5 4H6l.5-4H9v-2.1A5 5 0 0 1 5 7V4zm-3 1h2v2a2 2 0 0 1-2-2zm18 0h2a2 2 0 0 1-2 2V5z" /></svg>
              <span style={{ fontSize: 12, color: 'rgba(241,236,224,0.85)', letterSpacing: '-0.2px', fontVariantNumeric: 'tabular-nums' }}>
                솟다 <b style={{ color: 'var(--cs-gold-on-ink)' }}>상위 {bench.percentile}%</b>
                {bench.active_count > 0 && <> · {bench.active_count.toLocaleString()}명 중 {bench.rank}위</>}
              </span>
            </div>
          )}
          {/* 상대평가 정직성(결정 A) — 손실 중인데 백분위 상위 절반 이내일 때만 '선방'. 손실≠하위.
              ⛔ 시장판단·위로성 표현 영구 금지(효신님 2026-06-26): '약세장'·'다같이 어려운 장' 등 근거 유무와 무관하게 안 씀. 백분위는 순위·선방 사실만.
              손실+하위(percentile>50)면 미표시 → 거짓 위로 방지. */}
          {bench && bench.stage !== 'seed' && bench.percentile != null && pnl < 0 && bench.percentile <= 50 && (
            <div style={{ fontSize: 11, color: 'rgba(241,236,224,0.5)', marginTop: 6, letterSpacing: '-0.2px' }}>손실 중이어도 다른 유저보다 선방하고 있어요</div>
          )}
          {/* 투자가능 / 보유평가 — 흰 7% 내부 박스 (시안 정확값) */}
          {acc && (
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '11px 13px' }}>
                {/* 평상시(casual): 투자가능 = 활동 BULL − 구매분(cash_bull). §16-1 */}
                <div style={{ fontSize: 12, color: 'rgba(241,236,224,0.55)', fontWeight: 600, marginBottom: 4 }}>투자가능</div>
                <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px' }}>{fmtBull(acc.investable_bull ?? acc.cash_balance)}</div>
                {acc.cash_bull > 0 && (<div style={{ fontSize: 10, color: 'rgba(241,236,224,0.4)', marginTop: 2 }}>크레딧 {fmtBull(acc.cash_bull)} 제외</div>)}
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '11px 13px' }}>
                <div style={{ fontSize: 12, color: 'rgba(241,236,224,0.55)', fontWeight: 600, marginBottom: 4 }}>보유평가</div>
                <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px' }}><RollingNumber text={fmtBull(pf?.holdings_value_bull || 0)} /></div>
              </div>
            </div>
          )}
          {/* 복기 슬롯 — 보조 진입점(정본은 하단 복기 탭 · 2026-09-07). 거래가 없어도 사라지지 않고 빈 상태를 허브가 설명한다. */}
          {!loading ? (
            <button onClick={() => nav('/portfolio-analysis')} style={{ width: 'calc(100% + 44px)', margin: '16px -22px 0', border: 'none', borderTop: '1px solid rgba(241,236,224,0.13)', background: 'rgba(0,0,0,0.12)', color: 'var(--cs-on-ink)', display: 'flex', alignItems: 'center', gap: 9, padding: '13px 22px', cursor: 'pointer', borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cs-gold-on-ink)', flexShrink: 0, boxShadow: '0 0 0 3px rgba(230,196,128,0.2)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.2px', flex: 1, textAlign: 'left' }}>
                복기 · <b style={{ color: 'var(--cs-gold-on-ink)' }}>{lossCount > 0 ? `돌아볼 거래 ${lossCount}건` : '내 매매 복기하기'}</b>
              </span>
              <CSIcon name="arrow" size={16} color="rgba(241,236,224,0.5)" />
            </button>
          ) : <div style={{ height: 22 }} />}
        </div>
        )}

        {/* 파산 시 폐지수집소 안내 배너 (활동가능 총자산 ≤ 1000 = scrap route THRESHOLD).
            pf 존재 가드 필수 — 네트워크 에러를 파산으로 번역하지 않게 (UX 검수 결함 #2) */}
        {!loading && !err && pf && total <= 1000 && (
          <CSCard padded tone="sunken" onClick={() => nav('/scrap')} style={{ cursor: 'pointer' }}>{/* 왼쪽 fall 테두리 제거(§G-2 slop 트로프 · 2026-09-03) — 경계는 sunken 톤으로 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CSIcon name="trash" size={24} color="var(--cs-mute)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>폐지수집소 열림</div>
                <div style={{ fontSize: 12, color: 'var(--cs-mute)' }}>자산이 바닥났어요 · 출석·활동으로 복구하기</div>
              </div>
              <CSIcon name="arrow" size={18} />
            </div>
          </CSCard>
        )}

        {/* 보유 종목 — 매일 오는 유저의 시선 종착지라 도구 그리드보다 위 (UX 검수 P1: "내 종목이 메뉴판 아래 묻혔다") */}
        {/* ⚠ 2026-07-26: 여기 있던 '복기 →' 링크 제거. 히어로 하단 복기 슬롯과 **목적지가 완전히 동일**
            (/portfolio-analysis)해서 한 화면에 같은 진입점이 두 개였다(효신님 지적). 히어로 쪽을 남긴 이유 =
            '돌아볼 거래 N건' 배지를 달고 있어 정보량이 크고, 주석대로 그쪽이 허브 정본 진입점이다. */}
        <div>
          <SecHeader first>보유 종목 {positions.length > 0 && `(${positions.length})`}</SecHeader>
          {loading && <SkeletonCard lines={3} />}
          {!err && !loading && positions.length === 0 && (
            <CSCard padded>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, justifyContent: 'center', fontSize: 12.5, color: 'var(--cs-ink-2)', lineHeight: 1.5, padding: '6px 0 10px' }}>
                <EquippedCosmeticMini cosmetic={emptyCompanion} size={36} />
                <span>{companionLine(emptyCompanion.slug, emptyCompanion.grade, 'empty', new Date().getDate())}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--cs-mute)', textAlign: 'center', padding: '4px 0 12px' }}>
                아직 보유 종목이 없어요.<br />아래 <b>종목 검색</b>으로 첫 투자를 시작하세요 🐂
              </div>
            </CSCard>
          )}
          {positions.length > 0 && (
            <CSCard padded>
              {positions.map((p, i) => (
                <CSRow
                  key={`${p.asset_class}:${p.symbol}`}
                  left={<AssetIcon p={p} />}
                  onClick={() => nav(stockPath(p.symbol))}
                  divider={i < positions.length - 1}
                  title={nameOf(p.symbol)}
                  sub={`현재가 ${fmtPriceCompact(p.cur_price, p.currency)} · 평단 ${fmtPriceCompact(p.avg_price, p.currency)}${p.quote_ok ? '' : ' · 시세 지연'}`} // 현재가 노출(실시간 체감) — 투자원금은 평가액·손익%로 역산 가능
                  right={
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--cs-ink)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px' }}><RollingNumber text={fmtBull(p.value_bull)} /></div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: pnlColor(p.pnl_bull), fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                        {/* 효신님 결정: 손익 ▲%만 (수량·BULL 금액 제외) */}
                        {p.pnl_bull > 0 ? '▲' : p.pnl_bull < 0 ? '▼' : '·'} <RollingNumber text={`${Math.abs(p.pnl_pct).toFixed(2)}%`} />
                      </div>
                    </div>
                  }
                />
              ))}
            </CSCard>
          )}
        </div>

        {/* 거래하기 — 시안 1:1: 명조 헤더 + 잉크그린 풀폭 버튼(radius15·weight800·shadow). 발굴은 작은 링크(효신님 #9 강등) */}
        <div>
          <SecHeader>거래하기</SecHeader>
          <button onClick={() => { setSearch(''); setPickerOpen(true); }} style={{ width: '100%', border: 'none', background: 'var(--cs-accent)', color: 'var(--cs-accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 17, borderRadius: 15, cursor: 'pointer' }}>
            {/* §G-3: 일반 CTA는 그림자 0. 종전 인라인 `0 10px 22px -12px` 그림자는 로그인 상태에서만 렌더돼
                게스트 g:audit에 한 번도 안 잡혔다(2026-09-10 로그인 mock 감사로 발견). */}
            <CSIcon name="search" size={20} />
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' }}>종목 검색 · 차트 보기</span>
          </button>
          {/* ⚠ 2026-07-26: 여기 있던 '오늘의 종목 발굴하기 →' 회색 링크 제거 → 아래 [도구] 탭으로 이동.
              찾기 기능이 화면 세 군데(여기·참여탭·도구탭)에 흩어져 있던 걸 도구 한 곳에 모았다. */}
        </div>

        {/* 부가 진입점 세그먼트 [참여|도구|기록] — 스크롤 감축(§1.1 C안 · 도구3+참여4+기록2를 탭 분리).
            기존 3개 세로 섹션 → 세그먼트 1개 + 선택 탭 콘텐츠. 각 진입점 nav·라이브배지 배선 100% 보존.
            ⚠ 2026-07-26: 탭마다 콘텐츠 높이가 크게 다르다(참여 4행 · 도구 1줄 그리드 · 기록 2행).
              긴 탭에서 스크롤을 내린 뒤 짧은 탭으로 바꾸면 아래 내용이 사라지며 화면이 위로 딸려 올라가
              "지금 뭘 보고 있었는지" 기준을 잃었다(효신님 지적). 탭을 누르면 세그먼트 자체를 뷰포트
              상단으로 데려와 기준점을 고정한다. 최초 마운트에는 스크롤하지 않는다(유저 조작 때만). */}
        <div>
          <div ref={subTabRef} style={{ marginBottom: 12, scrollMarginTop: 12 }}>
            <CSTabs
              fullWidth
              items={[
                { value: 'join', label: '참여' },
                { value: 'tools', label: '도구' },
                { value: 'records', label: '기록' },
              ]}
              value={subTab}
              onChange={(v) => {
                setSubTab(v);
                subTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          </div>

          {/* ⚠ 2026-07-26 세 탭 시각 통일 (효신님 "아이콘·배경색·배치가 제각각이라 AI slop 같다") ─────
              BEFORE: 참여=리스트+tint 4색(accent/gold/bull/fall) · 도구=3칸 그리드+무배경 아이콘 ·
                      기록=리스트+tint 2색 → 한 세그먼트 안에 레이아웃 2종 + 아이콘 배경 6색.
              AFTER : 세 탭 모두 **같은 리스트(SubTabList)** · 아이콘 배경은 **무채색 surface-2 통일** ·
                      stroke 1.8 통일. docs/design.md §G-1 "캐릭터·카테고리 아이콘 배경은 무채색으로
                      통일 — 색은 일러스트/콘텐츠가 담당" 준수(기존 4색 tint가 이 룰 위반이었다).
              색은 라이브 배지(accent)만 남긴다 = 의미 있는 곳에만 색 → 강조가 다시 강조로 기능. */}
          {subTab === 'join' && (
            <SubTabList nav={nav} rows={[
              { label: '거시 이벤트방', sub: macroCount > 0 ? `투표 진행 중 ${macroCount}건` : '다음 이벤트 준비 중', live: macroCount > 0, to: '/macro-event', icon: <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></> },
              ...(buyOrNotVisible ? [{ label: '살까 말까 결정방', sub: buyOrNotCount > 0 ? `결정 대기 ${buyOrNotCount}건` : '새 결정을 기다리는 중', live: buyOrNotCount > 0, to: '/buy-or-not', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 15h.01" /></> }] : []),
              { label: '기록 미션', sub: '시즌 랭킹 · 미션 완주 횟수', to: '/challenge-board', icon: <><path d="M8 21h8M12 17v4" /><path d="M6 4h12v6a6 6 0 0 1-12 0z" /></> },
            ]} />
          )}

          {/* 도구 = '찾기 도구'를 한데 모음 (2026-07-26) ─────
              BEFORE: 발굴은 '거래하기' 아래 작은 회색 링크로 강등 · 레이더는 참여 탭 · 비교/알림/히트맵만 도구.
                      → 종목을 찾는 기능 5개가 화면 세 군데에 흩어져 둘 다 잘 안 보였다(효신님 ⑧ 지적).
              AFTER : 발굴·레이더·비교·알림·히트맵 = 도구 한 곳. 레이더를 맨 위에 둔다 —
                      리포트 시그널 기반이라 솟다에만 있는 기능이고, 발굴(등락순위·스크리너)은 어느 앱에나 있다.
              발굴과 레이더는 중복이 아니다: 발굴=내 유니버스 안 탐색 / 레이더=외부 리포트 시그널 스캔.
              그래서 합치지 않고 sub 문구로 역할을 구분해 나란히 둔다. */}
          {subTab === 'tools' && (
            <SubTabList nav={nav} rows={[
              { label: '소외주 레이더', sub: '리포트가 먼저 움직인 종목 스캔', to: '/turnaround-radar', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 12l5-5" /><path d="M12 3v2M21 12h-2M12 21v-2M3 12h2" /></> },
              { label: '오늘의 종목 발굴', sub: '관심종목 · 등락순위 · 조건 검색', to: '/discover', icon: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></> },
              { label: '종목 비교', sub: '2~3종목 6개월 상대 수익률', to: '/compare', icon: <path d="M6 20V10M12 20V4M18 20v-6" /> },
              { label: '가격 알림', sub: '목표가 도달 시 표시', to: '/alerts', icon: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></> },
              { label: '시장 히트맵', sub: '섹터별 등락 한눈에', to: '/heatmap', icon: <><rect x="3" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" /></> },
            ]} />
          )}

          {/* 기록 — 랭킹리더보드·실패박물관. 코스메틱은 MY탭 이동(효신님 #7·§11). */}
          {subTab === 'records' && (
            <SubTabList nav={nav} rows={[
              { label: '랭킹 리더보드', sub: '수익률 랭킹 보기', to: '/leaderboard', icon: <><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M6 4h12v5a6 6 0 0 1-12 0z" /><path d="M9 18h6M10 22h4M12 18v-3" /></> },
              { label: '실패 박물관', sub: lossCount > 0 ? `복기 대기 ${lossCount}건 · 손실 거래 다시 보기` : '손실 거래 다시 보기', to: '/failmuseum', icon: <path d="M3 21h18M5 21V8l7-4 7 4v13M9 21v-5h6v5" /> },
            ]} />
          )}
        </div>

        <div style={{ fontSize: 11, color: 'var(--cs-mute)', textAlign: 'center', lineHeight: 1.5, marginTop: 4 }}>
          가상자금(BULL) 모의투자 · 실제 거래 아님 · 투자권유 아님<br />
          시세 약 20분 지연 · 안목 등급은 며칠 운용 후 정밀 측정
        </div>
      </div>

      {/* 종목 선택 모달 → 종목 상세(차트) 진입 */}
      <CSModal open={pickerOpen} onClose={() => setPickerOpen(false)} title="종목 선택" sheet>
        <CSInput
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="종목명·티커 검색" prefix={<CSIcon name="search" size={16} />}
          style={{ marginBottom: 10 }}
        />
        {/* height 고정(maxHeight X) — 키보드가 떠도 리스트 공간 유지 + 타이핑 즉시 필터 결과 표시 */}
        <div style={{ height: '46vh', overflowY: 'auto' }}>
          {filtered.map((s) => (
            <CSRow
              key={s.symbol}
              onClick={() => { setPickerOpen(false); nav(stockPath(s.symbol)); }}
              title={s.name}
              sub={`${s.symbol} · ${MARKET_LABEL[s.market]}`}
              right={<CSBadge tone="ink">차트</CSBadge>}
            />
          ))}
          {/* 서버 전체 검색 결과 — 유니버스 밖 종목 (이름은 state로 전달, StockDetail meta 부재 폴백) */}
          {remoteOnly.length > 0 && (
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cs-mute)', padding: '10px 4px 4px' }}>전체 검색</div>
          )}
          {remoteOnly.map((s) => (
            <CSRow
              key={s.symbol}
              onClick={() => { setPickerOpen(false); nav(stockPath(s.symbol), { state: { name: s.name } }); }}
              title={s.name}
              sub={`${s.symbol} · ${MARKET_LABEL[s.market] || s.market}`}
              // 레버리지·인버스는 여기서만 만나므로(목록엔 안 넣는다) 배지를 이 자리에 단다
              right={isLeveragedEtf(s) ? <CSBadge tone="alert">{LEVERAGED_BADGE}</CSBadge> : <CSBadge tone="ink">차트</CSBadge>}
            />
          ))}
          {searching && (
            <div style={{ fontSize: 12.5, color: 'var(--cs-mute)', textAlign: 'center', padding: '10px 0' }}>전체 종목 검색 중…</div>
          )}
          {filtered.length === 0 && remoteOnly.length === 0 && !searching && (
            <div style={{ fontSize: 13, color: 'var(--cs-mute)', textAlign: 'center', padding: '24px 0' }}>검색 결과 없음</div>
          )}
        </div>
      </CSModal>

      {/* P2 §13 — 안목 등급 설명 모달 (홈 ★배지 탭) */}
      <CSModal open={gradeInfoOpen} onClose={() => setGradeInfoOpen(false)} title="안목 등급이란?" sheet>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--cs-ink-2)', lineHeight: 1.6 }}>
            {/* 🔴 2026-09-09: 종전엔 stage로만 분기해, 성장 단계에서 **절대 수익률로 등급이 정해졌는데도**
                "다른 유저 대비"라고 설명했다. 실제 채택 기준(byPercentile)으로 갈라야 문구가 사실과 맞는다. */}
            내 투자 <b>안목</b>을 한눈에 보여주는 등급이에요. 산정 기준은 <b>{resolvedGrade.basis}</b>이에요{resolvedGrade.byPercentile
              ? ' — 다른 솟다 유저 대비라 손실 중이어도 순위는 상위일 수 있어요'
              : (resolvedGrade.stage === 'seed'
                  ? ' — 솟다 유저가 늘면 다른 유저 대비 순위(백분위)로 정밀해져요'
                  : ' — 이번엔 유저 순위보다 수익률 쪽 등급이 높아 그쪽으로 매겼어요')}.
          </div>
          {/* V2 — 등급 산출 투명성: 성장·성숙 단계서 현재 순위를 중립 명시(시장판단·위로성 표현 영구 금지·효신님 결정 2026-06-26). 칩과 동일 게이트(씨앗 숨김). */}
          {bench && bench.stage !== 'seed' && bench.percentile != null && bench.active_count > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--cs-ink-2)', background: 'var(--cs-surface-2)', borderRadius: 'var(--cs-r-md)', padding: '10px 12px', lineHeight: 1.5, fontVariantNumeric: 'tabular-nums' }}>
              지금 <b>{bench.active_count.toLocaleString()}명 중 {bench.rank}위</b> · 솟다 <b style={{ color: 'var(--cs-accent)' }}>상위 {bench.percentile}%</b>예요
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {EYE_GRADE_TIERS.map((t) => {
              const on = t.key === grade.key;
              return (
                <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 'var(--cs-r-md)', border: `1px solid ${on ? 'var(--cs-accent)' : 'var(--cs-line)'}`, background: on ? 'var(--cs-surface-2)' : 'transparent' }}>
                  <span style={{ fontSize: 14, fontWeight: on ? 800 : 600, color: on ? 'var(--cs-accent)' : 'var(--cs-ink)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{on && <CSIcon name="star" size={13} />}{t.label}</span>
                  <span className="num" style={{ fontSize: 12, color: 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)' }}>{resolvedGrade.percentile == null ? (t.min === 0 ? '시작' : `+${t.min}%p~`) : '유저 백분위'}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--cs-mute)', lineHeight: 1.5, paddingTop: 2 }}>
분석 탭의 <b>매매 습관</b>(승률·손익비)은 '거래 과정'을 보는 진단이고, 이 <b>안목 등급</b>은 '성과(수익)'를 보는 거예요. 서로 다른 지표예요.
          </div>
        </div>
      </CSModal>
    </div>
  );
}
